import { useEffect, useRef, useState } from "react";
import { LayoutDashboard, FolderOpen, FilePlus2, Columns3, UsersRound, CalendarDays, ChartNoAxesCombined, SlidersHorizontal, ShieldCheck, Plus, X, Menu, LogOut, ArrowRight, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Pencil, Trash2, Search, Video, Clapperboard, Image, Usb, CircleCheck, Inbox, Check, Eye, EyeOff } from "lucide-react";
import { DEFAULT_OPTIONS, STATUS, STATUS_ORDER, TITLES, TODAY, TYPES, TYPE_ORDER, emptyForm } from "./data.js";
import { loadState, saveState } from "./storage.js";

const dirPalette = ["#5a62b0", "#55703f", "#a8553c", "#7c5aa8", "#3c8a8a", "#b0843c", "#a8456e", "#33691e"];
const shooterPalette = ["#b0843c", "#3c8a8a", "#a8456e", "#5a62b0", "#55703f", "#a8553c", "#7c5aa8", "#33691e"];
const DEFAULT_USERS = [
  {
    id: "u-admin",
    name: "管理者",
    username: "admin",
    password: "admin123",
    role: "admin",
    permissions: { cases: true, reports: true, settings: true, users: true }
  }
];
const PERMISSIONS = [
  ["cases", "案件"],
  ["reports", "報表"],
  ["settings", "設定"],
  ["users", "帳號"]
];

function fmt(date) {
  return date ? date.replaceAll("-", "/") : "";
}

function overall(item) {
  const values = Object.values(item.deliverables).map((value) => value === "review" ? "doing" : value);
  const usbRequired = item.deliverables.photos !== undefined && (item.usb || {}).required !== false;
  const usbComplete = !usbRequired || (item.usb || {}).completed === true;
  if (values.every((value) => value === "done") && usbComplete) return "done";
  if (values.every((value) => value === "todo")) return "todo";
  return "doing";
}

function statusChip(status) {
  const item = STATUS[status === "review" ? "doing" : status];
  return <span className="chip" style={{ background: item.bg, color: item.color }}>{item.label}</span>;
}

function typeChips(item) {
  return TYPE_ORDER.filter((type) => item.deliverables[type] !== undefined).map((type) => (
    <span key={type} className="chip" title={`${TYPES[type].label} - ${STATUS[item.deliverables[type] === "review" ? "doing" : item.deliverables[type]].label}`} style={{ background: STATUS[item.deliverables[type] === "review" ? "doing" : item.deliverables[type]].bg, color: STATUS[item.deliverables[type] === "review" ? "doing" : item.deliverables[type]].color }}>
      {TYPES[type].short}
    </span>
  ));
}

function toggleArray(list, value) {
  return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
}

export default function App() {
  const initial = loadState();
  const [page, setPage] = useState("dashboard");
  const [cases, setCases] = useState(initial.cases);
  const [options, setOptions] = useState({ ...DEFAULT_OPTIONS, ...initial.options });
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState("");
  const [caseSearch, setCaseSearch] = useState("");
  const [caseDirFilter, setCaseDirFilter] = useState("");
  const [staffSelected, setStaffSelected] = useState(options.directors[0] || "");
  const [reportMonth, setReportMonth] = useState(TODAY.slice(0, 7));
  const [reportGroupBy, setReportGroupBy] = useState("director");
  const [annualYear, setAnnualYear] = useState(TODAY.slice(0, 4));
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [newOption, setNewOption] = useState({});
  const [selectedCase, setSelectedCase] = useState(null);
  const [users, setUsers] = useState(initial.users?.length ? initial.users : DEFAULT_USERS);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginError, setLoginError] = useState("");
  const [navOpen, setNavOpen] = useState(false);
  const mainRef = useRef(null);

  useEffect(() => {
    setNavOpen(false);
    mainRef.current?.scrollTo(0, 0);
  }, [page]);

  useEffect(() => saveState({ cases, options, users }), [cases, options, users]);
  useEffect(() => {
    if (!currentUser) return;
    setCurrentUser(users.find((user) => user.id === currentUser.id) || null);
  }, [users, currentUser?.id]);

  const dirColor = (name) => dirPalette[Math.max(0, options.directors.indexOf(name)) % dirPalette.length] || "#17150e";
  const shooterColor = (name) => shooterPalette[Math.max(0, options.shooters.indexOf(name)) % shooterPalette.length] || "#17150e";

  let [pageTitle, pageSub] = TITLES[page] || ["", ""];
  if (page === "users") {
    pageTitle = "帳號權限";
    pageSub = "管理登入帳號、密碼與權限";
  }
  if (page === "newcase" && editingId) {
    pageTitle = "編輯案件";
    pageSub = "修改現有案件內容";
  }

  function startNewCase() {
    setEditingId(null);
    setForm(emptyForm());
    setFormError("");
    setPage("newcase");
  }

  function editCase(item) {
    const needs = {};
    TYPE_ORDER.forEach((type) => {
      needs[type] = item.deliverables[type] !== undefined;
    });
    setEditingId(item.id);
    setForm({
      name: item.name,
      religion: item.religion || "佛道教",
      caseAttribute: item.caseAttribute || "一般",
      date: item.date,
      location: item.location,
      venueStyles: [...(item.venueStyles || [])],
      extras: [...(item.extras || [])],
      director: item.director,
      assistant: item.assistant,
      emcee: item.emcee || "",
      shooter: item.shooter || "",
      returning: item.returning,
      note: item.note || "",
      needs
    });
    setFormError("");
    setPage("newcase");
  }

  function deleteCase(item) {
    if (!window.confirm(`確定要刪除「${item.name}」嗎？此動作無法復原。`)) return;
    setCases((current) => current.filter((caseItem) => caseItem.id !== item.id));
  }

  function submitCase() {
    if (!form.name.trim()) {
      setFormError("請輸入案名");
      return;
    }
    const need = TYPE_ORDER.filter((type) => form.needs[type]);
    if (need.length === 0) {
      setFormError("請至少選擇一項需求項目");
      return;
    }
    const deliverables = {};
    need.forEach((type) => {
      deliverables[type] = "todo";
    });
    if (editingId) {
      setCases((current) => current.map((item) => {
        if (item.id !== editingId) return item;
        const nextDeliverables = {};
        need.forEach((type) => {
          nextDeliverables[type] = item.deliverables[type] || "todo";
        });
        return { ...item, ...normalizedForm(), deliverables: nextDeliverables };
      }));
      setPage("cases");
    } else {
      setCases((current) => [{ id: `c${Date.now()}`, ...normalizedForm(), deliverables }, ...current]);
      setPage("kanban");
    }
    setEditingId(null);
    setForm(emptyForm());
    setFormError("");
  }

  function normalizedForm() {
    return {
      name: form.name.trim(),
      religion: form.religion || "佛道教",
      caseAttribute: form.caseAttribute || "一般",
      date: form.date,
      location: form.location || "未指定",
      venueStyles: form.venueStyles,
      extras: form.extras,
      director: form.director || "未指定",
      assistant: form.assistant || "未指定",
      emcee: form.emcee || "未指定",
      shooter: form.shooter || "未指定",
      returning: form.returning,
      note: form.note
    };
  }

  function moveCard(targetStatus) {
    if (!dragId) return;
    setCases((current) => current.map((item) => {
      if (item.id !== dragId) return item;
      const deliverables = {};
      Object.keys(item.deliverables).forEach((type) => {
        deliverables[type] = targetStatus;
      });
      return { ...item, deliverables };
    }));
    setDragId(null);
    setDragOver(null);
  }

  function toggleDeliverable(caseId, type, checked) {
    setCases((current) => current.map((item) => {
      if (item.id !== caseId) return item;
      return {
        ...item,
        deliverables: {
          ...item.deliverables,
          [type]: checked ? "done" : "todo"
        },
        completionDates: {
          ...(item.completionDates || {}),
          [type]: checked ? ((item.completionDates || {})[type] || TODAY) : ((item.completionDates || {})[type] || "")
        }
      };
    }));
  }

  function updateCompletionDate(caseId, type, date) {
    setCases((current) => current.map((item) => {
      if (item.id !== caseId) return item;
      return {
        ...item,
        deliverables: {
          ...item.deliverables,
          [type]: date ? "done" : "todo"
        },
        completionDates: {
          ...(item.completionDates || {}),
          [type]: date
        }
      };
    }));
  }

  function updateUsbDetail(caseId, key, value) {
    setCases((current) => current.map((item) => {
      if (item.id !== caseId) return item;
      const usb = { ...(item.usb || {}), [key]: value };
      if (key === "required") usb.completed = value ? false : true;
      return { ...item, usb };
    }));
  }

  function login(username, password) {
    const user = users.find((item) => item.username === username.trim() && item.password === password);
    if (!user) {
      setLoginError("帳號或密碼錯誤");
      return;
    }
    setCurrentUser(user);
    setLoginError("");
  }

  function logout() {
    setCurrentUser(null);
    setPage("dashboard");
  }

  if (!currentUser) {
    return <LoginScreen onLogin={login} error={loginError} />;
  }

  return (
    <div className={`app ${navOpen ? "nav-open" : ""}`}>
      {navOpen && <button className="nav-scrim" aria-label="關閉導覽" onClick={() => setNavOpen(false)} />}
      <Sidebar page={page} setPage={setPage} startNewCase={startNewCase} currentUser={currentUser} logout={logout} closeNav={() => setNavOpen(false)} />
      <div className="shell">
        <header className="topbar">
          <button className="icon-button mobile-menu" title="開啟導覽" aria-label="開啟導覽" aria-expanded={navOpen} onClick={() => setNavOpen(true)}><Menu size={20} /></button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="breadcrumb">華梵 MV <span>/</span> 工作空間</span>
          </div>
          <div className="row topbar-actions">
            <div className="today"><CalendarDays size={15} />{fmt(TODAY)}</div>
            {(currentUser.role === "admin" || currentUser.permissions?.cases) && <button className="primary-button" onClick={startNewCase}><Plus size={17} />新增案件</button>}
          </div>
        </header>
        <main className="main" ref={mainRef} id="main-content">
          <div className="page-heading"><div><div className="eyebrow">CASE MANAGEMENT</div><h1 className="page-title">{pageTitle}</h1><p className="page-sub">{pageSub}</p></div><span className="page-index">{String(["dashboard", "cases", "newcase", "kanban", "staff", "monthly", "annual", "settings", "users"].indexOf(page) + 1).padStart(2, "0")}</span></div>
          {page === "dashboard" && <Dashboard cases={cases} dirColor={dirColor} />}
          {page === "cases" && <CasesPage cases={cases} options={options} caseSearch={caseSearch} setCaseSearch={setCaseSearch} caseDirFilter={caseDirFilter} setCaseDirFilter={setCaseDirFilter} editCase={editCase} deleteCase={deleteCase} openCase={setSelectedCase} dirColor={dirColor} />}
          {page === "newcase" && <CaseForm form={form} setForm={setForm} options={options} formError={formError} submitCase={submitCase} cancel={() => { setPage("cases"); setEditingId(null); setForm(emptyForm()); }} submitLabel={editingId ? "儲存變更" : "建立案件"} />}
          {page === "kanban" && <KanbanMerged cases={cases} dragOver={dragOver} setDragOver={setDragOver} setDragId={setDragId} moveCard={moveCard} toggleDeliverable={toggleDeliverable} updateCompletionDate={updateCompletionDate} updateUsbDetail={updateUsbDetail} dirColor={dirColor} />}
          {page === "staff" && <Staff cases={cases} options={options} selected={staffSelected} setSelected={setStaffSelected} dirColor={dirColor} />}
          {page === "monthly" && <Monthly cases={cases} options={options} reportMonth={reportMonth} setReportMonth={setReportMonth} reportGroupBy={reportGroupBy} setReportGroupBy={setReportGroupBy} dirColor={dirColor} shooterColor={shooterColor} />}
          {page === "annual" && <Annual cases={cases} annualYear={annualYear} setAnnualYear={setAnnualYear} />}
          {page === "settings" && <SettingsEditor options={options} setOptions={setOptions} newOption={newOption} setNewOption={setNewOption} />}
          {page === "users" && <UserAdmin users={users} setUsers={setUsers} currentUser={currentUser} />}
        </main>
        {selectedCase && <CaseModal item={selectedCase} onClose={() => setSelectedCase(null)} dirColor={dirColor} />}
      </div>
    </div>
  );
}

function LoginScreen({ onLogin, error }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  function submit(event) {
    event.preventDefault();
    onLogin(username, password);
  }

  return (
    <div className="login-page">
      <div className="login-masthead"><span>華梵 MV</span><span>影像案件管理</span></div>
      <form className="login-card" onSubmit={submit}>
        <div className="brand" style={{ padding: 0, marginBottom: 22 }}>
          <div className="brand-mark">W</div>
          <div>
            <div className="brand-name">華梵MV</div>
            <div className="brand-sub">CASE SYSTEM</div>
          </div>
        </div>
        <div className="eyebrow">WELCOME BACK</div>
        <h1 className="page-title">每一份託付，<br />用心記錄。</h1>
        <p className="page-sub login-intro">登入華梵影像案件管理系統</p>
        <label className="field-label" htmlFor="login-username">帳號</label>
        <input id="login-username" className="field" required value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" placeholder="輸入帳號" />
        <label className="field-label" htmlFor="login-password" style={{ marginTop: 20 }}>密碼</label>
        <div className="password-field"><input id="login-password" className="field" required type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" placeholder="輸入密碼" /><button className="icon-button" type="button" title={showPassword ? "隱藏密碼" : "顯示密碼"} aria-label={showPassword ? "隱藏密碼" : "顯示密碼"} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div>
        {error && <div className="error-message" role="alert">{error}</div>}
        <button className="primary-button login-submit" type="submit">登入工作空間<ArrowRight size={18} /></button>
      </form>
      <div className="login-footer">華梵 MV <span>・</span> CASE SYSTEM</div>
    </div>
  );
}

function UserAdmin({ users, setUsers, currentUser }) {
  const [draft, setDraft] = useState({
    name: "",
    username: "",
    password: "",
    role: "staff",
    permissions: { cases: true, reports: false, settings: false, users: false }
  });
  const [error, setError] = useState("");

  function updateUser(id, patch) {
    setUsers((current) => current.map((user) => user.id === id ? { ...user, ...patch } : user));
  }

  function setPermission(id, key, checked) {
    setUsers((current) => current.map((user) => {
      if (user.id !== id) return user;
      return { ...user, permissions: { ...(user.permissions || {}), [key]: checked } };
    }));
  }

  function addUser(event) {
    event.preventDefault();
    const username = draft.username.trim();
    if (!draft.name.trim() || !username || !draft.password) {
      setError("請填寫名稱、帳號與密碼");
      return;
    }
    if (users.some((user) => user.username === username)) {
      setError("帳號已存在");
      return;
    }
    setUsers((current) => [...current, {
      ...draft,
      id: `u${Date.now()}`,
      name: draft.name.trim(),
      username
    }]);
    setDraft({ name: "", username: "", password: "", role: "staff", permissions: { cases: true, reports: false, settings: false, users: false } });
    setError("");
  }

  function removeUser(id) {
    if (id === currentUser.id) return;
    setUsers((current) => current.filter((user) => user.id !== id));
  }

  return (
    <div className="stack" style={{ maxWidth: 1080, gap: 18 }}>
      <form className="card" onSubmit={addUser}>
        <PanelTitle title="新增使用者" />
        <div className="form-grid">
          <div>
            <label className="field-label">名稱</label>
            <input className="field" value={draft.name} onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))} />
          </div>
          <div>
            <label className="field-label">帳號</label>
            <input className="field" value={draft.username} onChange={(event) => setDraft((current) => ({ ...current, username: event.target.value }))} />
          </div>
          <div>
            <label className="field-label">密碼</label>
            <input className="field" type="password" value={draft.password} onChange={(event) => setDraft((current) => ({ ...current, password: event.target.value }))} />
          </div>
          <div>
            <label className="field-label">角色</label>
            <select className="select" value={draft.role} onChange={(event) => setDraft((current) => ({ ...current, role: event.target.value }))}>
              <option value="admin">管理者</option>
              <option value="staff">一般使用者</option>
              <option value="viewer">檢視者</option>
            </select>
          </div>
          <div className="full">
            <label className="field-label">權限</label>
            <div className="permission-list">
              {PERMISSIONS.map(([key, label]) => (
                <label key={key} className="permission-item">
                  <input type="checkbox" checked={!!draft.permissions[key]} onChange={(event) => setDraft((current) => ({ ...current, permissions: { ...current.permissions, [key]: event.target.checked } }))} />
                  {label}
                </label>
              ))}
            </div>
          </div>
        </div>
        {error && <div style={{ color: "#b4453a", marginTop: 14, fontSize: 14 }}>{error}</div>}
        <button className="primary-button" style={{ marginTop: 20 }} type="submit">新增使用者</button>
      </form>

      <div className="card">
        <PanelTitle title="使用者清單" />
        <div className="user-list">
          {users.map((user) => (
            <div key={user.id} className="user-row">
              <div>
                <label className="field-label">名稱</label>
                <input className="field" value={user.name} onChange={(event) => updateUser(user.id, { name: event.target.value })} />
              </div>
              <div>
                <label className="field-label">帳號</label>
                <input className="field" value={user.username} onChange={(event) => updateUser(user.id, { username: event.target.value.trim() })} />
              </div>
              <div>
                <label className="field-label">新密碼</label>
                <input className="field" type="password" placeholder="留空不修改" onChange={(event) => event.target.value && updateUser(user.id, { password: event.target.value })} />
              </div>
              <div>
                <label className="field-label">角色</label>
                <select className="select" value={user.role} onChange={(event) => updateUser(user.id, { role: event.target.value })}>
                  <option value="admin">管理者</option>
                  <option value="staff">一般使用者</option>
                  <option value="viewer">檢視者</option>
                </select>
              </div>
              <div className="permission-list" style={{ gridColumn: "1 / -1" }}>
                {PERMISSIONS.map(([key, label]) => (
                  <label key={key} className="permission-item">
                    <input type="checkbox" checked={!!(user.permissions || {})[key]} onChange={(event) => setPermission(user.id, key, event.target.checked)} />
                    {label}
                  </label>
                ))}
                <button className="mini-button danger" type="button" disabled={user.id === currentUser.id} onClick={() => removeUser(user.id)}>刪除</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const NAV_ICONS = { dashboard: LayoutDashboard, cases: FolderOpen, newcase: FilePlus2, kanban: Columns3, staff: UsersRound, monthly: CalendarDays, annual: ChartNoAxesCombined, settings: SlidersHorizontal, users: ShieldCheck };

function Sidebar({ page, setPage, startNewCase, currentUser, logout, closeNav }) {
  const items = [["dashboard", "儀表板"], ["cases", "案件管理"], ["newcase", "案件建檔"], ["kanban", "處理進度"], ["staff", "禮儀師查詢"], ["monthly", "月報表"], ["annual", "年度統計"], ["settings", "下拉選單設定"]];
  return (
    <aside className="sidebar">
      <button className="icon-button nav-close" aria-label="關閉導覽" onClick={closeNav}><X size={20} /></button>
      <div className="brand">
        <div className="brand-mark">W</div>
        <div>
          <div className="brand-name">華梵MV</div>
          <div className="brand-sub">CASE SYSTEM</div>
        </div>
      </div>
      <nav aria-label="主要導覽">
      {[...items, ...(currentUser.permissions?.users || currentUser.role === "admin" ? [["users", "帳號權限"]] : [])].filter(([key]) => currentUser.role === "admin" || key === "dashboard" || (["cases", "newcase", "kanban", "staff"].includes(key) && currentUser.permissions?.cases) || (["monthly", "annual"].includes(key) && currentUser.permissions?.reports) || (key === "settings" && currentUser.permissions?.settings) || (key === "users" && currentUser.permissions?.users)).map(([key, label]) => {
        const Icon = NAV_ICONS[key];
        return <div key={key}>{["dashboard", "monthly", "settings"].includes(key) && <div className="nav-group-label">{key === "dashboard" ? "日常工作" : key === "monthly" ? "統計與報表" : "系統管理"}</div>}<button className={`nav-button ${page === key ? "active" : ""}`} aria-current={page === key ? "page" : undefined} onClick={() => { key === "newcase" ? startNewCase() : setPage(key); closeNav(); }}><Icon size={18} strokeWidth={1.65} /><span>{label}</span>{page === key && <span className="nav-indicator" />}</button></div>;
      })}
      </nav>
      <div className="sidebar-account"><span className="avatar">{currentUser.name.slice(0, 1)}</span><div><strong>{currentUser.name}</strong><small>{currentUser.role === "admin" ? "管理者" : currentUser.role === "viewer" ? "檢視者" : "一般使用者"}</small></div><button className="icon-button" title="登出" aria-label="登出" onClick={logout}><LogOut size={18} /></button></div>
    </aside>
  );
}

function Dashboard({ cases, dirColor }) {
  const monthCases = cases.filter((item) => item.date.startsWith(TODAY.slice(0, 7)));
  const doneCount = cases.filter((item) => overall(item) === "done").length;
  const doingCount = cases.filter((item) => overall(item) === "doing").length;
  const reminders = cases.filter((item) => item.date <= TODAY && overall(item) !== "done");
  const usbQty = (item) => (item.usb || {}).required === false ? 0 : (Number((item.usb || {}).quantity || 0) || 0);
  const currentMonth = TODAY.slice(0, 7);
  const currentYear = TODAY.slice(0, 4);
  const monthUsbCount = cases.filter((item) => item.date.startsWith(currentMonth)).reduce((sum, item) => sum + usbQty(item), 0);
  const yearUsbCount = cases.filter((item) => item.date.startsWith(currentYear)).reduce((sum, item) => sum + usbQty(item), 0);
  const totalUsbCount = cases.reduce((sum, item) => sum + usbQty(item), 0);
  const stats = [
    ["本月案件總數", monthCases.length, `系統累計 ${cases.length} 件`],
    ["進行中", doingCount, "製作流程尚未完成"],
    ["已完成", doneCount, "所有交付項目完成"],
    ["未完成提醒", reminders.length, "告別式已過"],
    ["回頭件", cases.filter((item) => item.returning).length, "累計回流客戶"],
    ["本月 USB", monthUsbCount, `${currentMonth.replace("-", " / ")} 數量`],
    ["今年 USB", yearUsbCount, `${currentYear} 年累計`],
    ["USB 總數", totalUsbCount, "全部案件累計"]
  ];
  const visibleStats = stats.filter(([label]) => !label.includes("USB"));
  const todos = buildTodos(cases).slice(0, 7);
  const recent = [...cases].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6);

  return (
    <div className="stack">
      <div className="stat-grid">{visibleStats.map(([label, value, sub]) => <Stat key={label} label={label} value={value} sub={sub} />)}</div>
      <UsbSummaryCard month={monthUsbCount} year={yearUsbCount} total={totalUsbCount} monthLabel={currentMonth.replace("-", " / ")} yearLabel={currentYear} />
      <div>
        <div className="section-label">本月製作概況</div>
        <div className="grid-3">{TYPE_ORDER.map((type) => <ProductionCard key={type} type={type} cases={monthCases} />)}</div>
      </div>
      <div className="grid-2">
        <div className="card">
          <PanelTitle title="今日待辦" meta={`共 ${todos.length} 項`} />
          {!todos.length && <EmptyState icon={CalendarDays} title="暫無近期拍攝安排" />}
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{todos.map((todo, index) => <Todo key={index} item={todo} />)}</div>
        </div>
        <div className="card">
          <PanelTitle title="未完成提醒" meta={<span className="chip" style={{ background: "var(--accent)", color: "#17150e" }}>{reminders.length}</span>} />
          {!reminders.length && <EmptyState icon={CircleCheck} title="目前沒有逾期案件" />}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>{reminders.map((item) => <Reminder key={item.id} item={item} />)}</div>
        </div>
      </div>
      <div className="card">
        <PanelTitle title="近期案件" />
        {!recent.length && <EmptyState title="尚無案件" />}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {recent.map((item) => (
            <div key={item.id} className="row" style={{ padding: "12px 0", borderTop: "1px solid var(--line)" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{item.name}</div>
                <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 3 }}>{fmt(item.date)} · <span style={{ color: dirColor(item.director), fontWeight: 600 }}>{item.director}</span></div>
              </div>
              <div className="row" style={{ gap: 6 }}>{typeChips(item)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  const Icon = label.includes("完成") ? CircleCheck : label.includes("進行") ? Clapperboard : label.includes("回頭") ? UsersRound : FolderOpen;
  return <div className="stat-card"><div className="stat-label"><span className="label">{label}</span><Icon size={17} /></div><div className="big-value">{value}</div><div className="stat-sub">{sub}</div></div>;
}

function EmptyState({ icon: Icon = Inbox, title }) {
  return <div className="empty-state"><Icon size={26} strokeWidth={1.4} /><span>{title}</span></div>;
}

function UsbSummaryCard({ month, year, total, monthLabel, yearLabel }) {
  return (
    <div className="usb-summary-card">
      <div>
        <div className="usb-heading"><Usb size={21} /><h2>USB 統計</h2></div>
        <div className="stat-sub">交付數量</div>
      </div>
      <div className="usb-summary-grid">
        <div>
          <div className="big-value">{month}</div>
          <div className="usb-summary-sub">本月 · {monthLabel}</div>
        </div>
        <div>
          <div className="big-value">{year}</div>
          <div className="usb-summary-sub">今年 · {yearLabel}</div>
        </div>
        <div>
          <div className="big-value">{total}</div>
          <div className="usb-summary-sub">總數</div>
        </div>
      </div>
    </div>
  );
}

function ProductionCard({ type, cases }) {
  const related = cases.filter((item) => item.deliverables[type] !== undefined);
  const done = related.filter((item) => item.deliverables[type] === "done").length;
  const pct = related.length ? Math.round((done / related.length) * 100) : 0;
  const Icon = type === "recording" ? Video : type === "editing" ? Clapperboard : Image;
  return <div className="production-card"><div className="row"><Icon size={19} /><span>{TYPES[type].label}</span><span className="production-count">{done} / {related.length}</span></div><div className="production-value">{pct}<span>%</span></div><div className="progress-track" role="progressbar" aria-label={TYPES[type].label} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><div style={{ width: `${pct}%`, background: TYPES[type].color }} /></div></div>;
}

function PanelTitle({ title, meta }) {
  return <div className="panel-title"><h2>{title}</h2>{meta && <div className="panel-meta">{meta}</div>}</div>;
}

function buildTodos(cases) {
  const todos = [];
  cases.filter((item) => item.date === TODAY).forEach((item) => todos.push({ title: item.name, meta: "今日告別式 · 需全程錄影", tag: "今日", color: "#fff", bg: "#17150e" }));
  cases.filter((item) => {
    const days = (Date.parse(item.date) - Date.parse(TODAY)) / 864e5;
    return days > 0 && days <= 4;
  }).forEach((item) => todos.push({ title: item.name, meta: `${fmt(item.date)} 告別式 · 預備拍攝`, tag: "即將", color: "#506b88", bg: "#E6EDF3" }));
  return todos;
}

function Todo({ item }) {
  return <div className="row" style={{ padding: "13px 15px", background: "var(--surface2)", borderRadius: 16 }}><div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14.5, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.title}</div><div style={{ fontSize: 12.5, color: "var(--slate)", marginTop: 3 }}>{item.meta}</div></div><span className="chip" style={{ color: item.color, background: item.bg }}>{item.tag}</span></div>;
}

function Reminder({ item }) {
  const days = Math.round((Date.parse(TODAY) - Date.parse(item.date)) / 864e5);
  const pending = TYPE_ORDER.filter((type) => item.deliverables[type] !== undefined && item.deliverables[type] !== "done").map((type) => TYPES[type].short).join("、");
  return <div className="row" style={{ gap: 13, padding: "13px 15px", background: "var(--surface2)", borderRadius: 16 }}><span style={{ width: 6, alignSelf: "stretch", background: days >= 5 ? "#17150e" : "#83771c", borderRadius: 2 }} /><div style={{ flex: 1, minWidth: 0 }}><div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><div style={{ fontSize: 14.5, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div><div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted)" }}>{days <= 0 ? "今日" : `已過 ${days} 天`}</div></div><div style={{ fontSize: 12.5, color: "var(--slate)", marginTop: 3 }}>{item.location} · {item.director}</div><div style={{ fontSize: 12.5, color: "#83771c", marginTop: 3 }}>未完成：{pending}</div></div></div>;
}

function CasesPage({ cases, options, caseSearch, setCaseSearch, caseDirFilter, setCaseDirFilter, editCase, deleteCase, openCase, dirColor }) {
  const filtered = cases.filter((item) => {
    const query = caseSearch.trim();
    return (!query || item.name.includes(query) || item.location.includes(query)) && (!caseDirFilter || item.director === caseDirFilter);
  }).sort((a, b) => b.date.localeCompare(a.date));
  return (
    <div className="stack" style={{ gap: 20 }}>
      <div className="row filter-bar" style={{ alignItems: "stretch" }}>
        <div className="search-field"><Search size={17} /><input className="search" aria-label="搜尋案名或地點" value={caseSearch} onChange={(event) => setCaseSearch(event.target.value)} placeholder="搜尋案名或地點…" /></div>
        <select className="search" aria-label="篩選禮儀師" style={{ flex: "none", minWidth: 180 }} value={caseDirFilter} onChange={(event) => setCaseDirFilter(event.target.value)}>
          <option value="">全部禮儀師</option>
          {options.directors.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
      </div>
      <div className="label">共 {filtered.length} 件案件</div>
      {!filtered.length && <EmptyState title={caseSearch || caseDirFilter ? "沒有符合條件的案件" : "尚無案件"} />}
      <div className="table cases-table">
        <div className="table-head" style={{ gridTemplateColumns: "1.35fr 120px 1fr 110px 1fr 130px" }}><div>案名</div><div>告別式日期</div><div>地點</div><div>禮儀師</div><div>處理進度</div><div style={{ textAlign: "right" }}>操作</div></div>
        {filtered.map((item) => (
          <div key={item.id} className="table-row" role="button" tabIndex={0} aria-label={`查看案件 ${item.name}`} onKeyDown={(event) => { if (event.target === event.currentTarget && ["Enter", " "].includes(event.key)) { event.preventDefault(); openCase(item); } }} onClick={() => openCase(item)} style={{ gridTemplateColumns: "1.35fr 120px 1fr 110px 1fr 130px", cursor: "pointer" }}>
            <div><div style={{ fontWeight: 500 }}>{item.name}</div>{item.returning && <span className="chip" style={{ marginTop: 6, background: "#F3F1D2", color: "#83771c" }}>回頭件</span>}</div>
            <div data-label="告別式日期">{fmt(item.date)}</div>
            <div data-label="地點" className="muted">{item.location}</div>
            <div data-label="禮儀師" style={{ color: dirColor(item.director), fontWeight: 600 }}>{item.director}</div>
            <div data-label="處理進度">{statusChip(overall(item))}</div>
            <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}><button className="icon-button" title="編輯案件" aria-label={`編輯 ${item.name}`} onClick={(event) => { event.stopPropagation(); editCase(item); }}><Pencil size={16} /></button><button className="icon-button danger" title="刪除案件" aria-label={`刪除 ${item.name}`} onClick={(event) => { event.stopPropagation(); deleteCase(item); }}><Trash2 size={16} /></button></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CaseModal({ item, onClose, dirColor }) {
  const dialogRef = useRef(null);
  useEffect(() => {
    const previous = document.activeElement;
    dialogRef.current?.focus();
    return () => previous?.focus();
  }, []);
  function handleKey(event) {
    if (event.key === "Escape") onClose();
    if (event.key === "Tab") {
      const controls = dialogRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex="0"]');
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="case-modal" ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="case-modal-title" tabIndex={-1} onKeyDown={handleKey} onClick={(event) => event.stopPropagation()}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 18 }}>
          <div>
            <h2 id="case-modal-title" style={{ fontSize: 24, fontWeight: 600 }}>{item.name}</h2>
            <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 5 }}>案件卡片</div>
          </div>
          <button className="icon-button" title="關閉案件卡片" aria-label="關閉案件卡片" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-fields">
          <ModalField label="告別式日期" value={fmt(item.date)} />
          <ModalField label="地點" value={item.location} />
          <ModalField label="禮儀師" value={<span style={{ color: dirColor(item.director), fontWeight: 600 }}>{item.director}</span>} />
          <ModalField label="跟件助理" value={item.assistant || "未指定"} />
          <ModalField label="司儀" value={item.emcee || "未指定"} />
          <ModalField label="拍攝者" value={item.shooter || "未指定"} />
          <ModalField label="宗教" value={item.religion || "佛道教"} />
          <ModalField label="案件屬性" value={item.caseAttribute || "一般"} />
        </div>
        <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid var(--line)" }}>
          <div className="label" style={{ marginBottom: 10 }}>處理進度</div>
          <div>{statusChip(overall(item))}</div>
        </div>
        {(item.venueStyles?.length || item.extras?.length || item.note) && (
          <div style={{ marginTop: 18, paddingTop: 18, borderTop: "1px solid var(--line)" }}>
            {!!item.venueStyles?.length && <ModalField label="會場樣式" value={item.venueStyles.join("、")} />}
            {!!item.extras?.length && <ModalField label="其他" value={item.extras.join("、")} />}
            {item.note && <ModalField label="備註" value={item.note} />}
          </div>
        )}
      </div>
    </div>
  );
}

function ModalField({ label, value }) {
  return (
    <div className="modal-field">
      <div className="label" style={{ marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: 14.5, lineHeight: 1.45 }}>{value}</div>
    </div>
  );
}

function CaseForm({ form, setForm, options, formError, submitCase, cancel, submitLabel }) {
  const update = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  return (
    <div className="form-card">
      <div className="form-grid">
        <h2 className="form-section-title full"><span>01</span>案件資料</h2>
        <Field label="案名"><input className="field" value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="例：故 林清水 先生 告別式" /></Field>
        <Field label="宗教"><Select value={form.religion} onChange={(value) => update("religion", value)} items={options.religions || []} placeholder="請選擇宗教" /></Field>
        <Field label="案件屬性"><Select value={form.caseAttribute} onChange={(value) => update("caseAttribute", value)} items={options.caseAttributes || []} placeholder="請選擇案件屬性" /></Field>
        <Field label="告別式日期"><input className="field" type="date" value={form.date} onChange={(event) => update("date", event.target.value)} /></Field>
        <Field label="地點"><Select value={form.location} onChange={(value) => update("location", value)} items={options.locations} placeholder="請選擇地點" /></Field>
        <h2 className="form-section-title full"><span>02</span>工作人員</h2>
        <Field label="禮儀師"><Select value={form.director} onChange={(value) => update("director", value)} items={options.directors} placeholder="請選擇禮儀師" /></Field>
        <Field label="跟件助理"><Select value={form.assistant} onChange={(value) => update("assistant", value)} items={options.assistants} placeholder="請選擇助理" /></Field>
        <Field label="司儀"><Select value={form.emcee} onChange={(value) => update("emcee", value)} items={options.emcees} placeholder="請選擇司儀" /></Field>
        <Field label="拍攝者"><Select value={form.shooter} onChange={(value) => update("shooter", value)} items={options.shooters} placeholder="請選擇拍攝者" /></Field>
        <h2 className="form-section-title full"><span>03</span>製作與現場需求</h2>
        <div className="full"><span className="field-label">需求項目</span><div className="choice-list">{TYPE_ORDER.map((type) => <label key={type} className={`pill-option ${form.needs[type] ? "on" : ""}`}><input type="checkbox" checked={form.needs[type]} onChange={(event) => update("needs", { ...form.needs, [type]: event.target.checked })} />{TYPES[type].label}</label>)}</div></div>
        <div className="full"><span className="field-label">會場樣式（可複選）</span><OptionPills items={options.venueStyles} selected={form.venueStyles} onToggle={(value) => update("venueStyles", toggleArray(form.venueStyles, value))} /></div>
        <div className="full"><span className="field-label">其他（可複選）</span><OptionPills items={options.extras} selected={form.extras} onToggle={(value) => update("extras", toggleArray(form.extras, value))} /></div>
        <label className="full row"><input type="checkbox" checked={form.returning} onChange={(event) => update("returning", event.target.checked)} /><span>回頭件</span></label>
        <Field className="full" label="備註"><textarea className="textarea" rows="3" value={form.note} onChange={(event) => update("note", event.target.value)} placeholder="家屬特殊需求、影片風格說明…" /></Field>
      </div>
      {formError && <div style={{ color: "#b4453a", marginTop: 16, fontSize: 14 }}>{formError}</div>}
      <div className="form-actions"><button className="primary-button" onClick={submitCase}><Check size={17} />{submitLabel}</button><button className="outline-button" onClick={cancel}>取消</button></div>
    </div>
  );
}

function Field({ label, className = "", children }) {
  return <label className={className}><span className="field-label">{label}</span>{children}</label>;
}

function Select({ value, onChange, items, placeholder }) {
  return <select className="select" value={value} onChange={(event) => onChange(event.target.value)}><option value="">{placeholder}</option>{items.map((item) => <option key={item} value={item}>{item}</option>)}</select>;
}

function OptionPills({ items, selected, onToggle }) {
  return <div className="choice-list">{items.map((item) => <label key={item} className={`pill-option ${selected.includes(item) ? "on" : ""}`}><input type="checkbox" checked={selected.includes(item)} onChange={() => onToggle(item)} />{item}</label>)}</div>;
}

function KanbanMerged({ cases, dragOver, setDragOver, setDragId, moveCard, toggleDeliverable, updateCompletionDate, updateUsbDetail, dirColor }) {
  const [expandedDone, setExpandedDone] = useState([]);
  const [editingCard, setEditingCard] = useState(null);

  return (
    <div className="stack" style={{ maxWidth: "none" }}>
      <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>
        {TYPE_ORDER.map((type) => (
          <div key={type} className="row" style={{ fontSize: 13 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: TYPES[type].color }} />
            {TYPES[type].label}
          </div>
        ))}
      </div>
      <div className="kanban">
        {STATUS_ORDER.map((status) => {
          const cards = cases.filter((item) => (editingCard?.id === item.id ? editingCard.status : overall(item)) === status);
          return (
            <div key={status} className="kanban-col" onDragOver={(event) => { event.preventDefault(); setDragOver(status); }} onDrop={() => moveCard(status)}>
              <div className="row" style={{ marginBottom: 10 }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: STATUS[status].color }} />
                <span style={{ fontWeight: 600 }}>{STATUS[status].label}</span>
                <span className="chip" style={{ marginLeft: "auto", color: STATUS[status].color, background: STATUS[status].bg }}>{cards.length}</span>
              </div>
              <div className="kanban-body" style={{ background: dragOver === status ? "rgba(23,21,14,.04)" : "transparent", border: `1.5px dashed ${dragOver === status ? "#17150e" : "transparent"}` }}>
                {!cards.length && <EmptyState title="暫無案件" />}
                {cards.map((item) => {
                  const isDone = status === "done";
                  const isExpanded = expandedDone.includes(item.id);
                  return (
                  <div key={item.id} className={`kanban-card ${isDone && !isExpanded ? "kanban-card-collapsed" : ""}`} draggable onFocusCapture={() => setEditingCard((current) => current?.id === item.id ? current : { id: item.id, status })} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setEditingCard(null); }} onDragStart={() => setDragId(item.id)} onDragEnd={() => setDragOver(null)}>
                    <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontWeight: 500, lineHeight: 1.35 }}>{item.name}</div>
                      {isDone && (
                        <div className="row" style={{ gap: 8, flex: "none" }}>
                          <span style={{ color: dirColor(item.director), fontSize: 12.5, fontWeight: 600 }}>{item.director}</span>
                          <button className="collapse-button" type="button" title={isExpanded ? "收合完成項目" : "展開完成項目"} aria-label={isExpanded ? "收合完成項目" : "展開完成項目"} aria-expanded={isExpanded} onClick={(event) => { event.stopPropagation(); setExpandedDone((current) => toggleArray(current, item.id)); }}>{isExpanded ? <ChevronUp size={17} /> : <ChevronDown size={17} />}</button>
                        </div>
                      )}
                    </div>
                    {!isDone && <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 8 }}>{fmt(item.date)} · <span style={{ color: dirColor(item.director), fontWeight: 600 }}>{item.director}</span></div>}
                    {(!isDone || isExpanded) && <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 13, paddingTop: 12, borderTop: "1px solid var(--line)" }}>
                      {TYPE_ORDER.filter((type) => item.deliverables[type] !== undefined).map((type) => {
                        const done = item.deliverables[type] === "done";
                        const usbRequired = (item.usb || {}).required !== false;
                        const usbCompleted = (item.usb || {}).completed === true || !usbRequired;
                        return (
                          <div key={type} className="deliverable-row" style={{ color: done ? "var(--muted)" : "var(--ink)" }}>
                            <input type="checkbox" aria-label={`${TYPES[type].label} 已完成`} checked={done} onChange={(event) => toggleDeliverable(item.id, type, event.target.checked)} style={{ width: 16, height: 16, accentColor: "#17150e", cursor: "pointer" }} />
                            <span style={{ width: 10, height: 10, borderRadius: 3, background: TYPES[type].color, flex: "none" }} />
                            <span style={{ textDecoration: done ? "line-through" : "none" }}>{TYPES[type].label}</span>
                            <input type="date" aria-label={`${TYPES[type].label} 完成日期`} value={(item.completionDates || {})[type] || ""} onChange={(event) => updateCompletionDate(item.id, type, event.target.value)} style={{ width: "100%", minWidth: 0, padding: "6px 8px", border: "1px solid var(--line)", borderRadius: 10, background: "#fff", color: "var(--ink)", fontSize: 12.5 }} />
                            {type === "photos" && (
                              <div className="usb-row">
                                <input type="checkbox" aria-label="USB 已完成" checked={usbCompleted} disabled={!usbRequired} onChange={(event) => updateUsbDetail(item.id, "completed", event.target.checked)} style={{ width: 16, height: 16, accentColor: "#17150e", cursor: usbRequired ? "pointer" : "default" }} />
                                <span style={{ fontSize: 13.5, color: "var(--ink)" }}>USB</span>
                                <select aria-label="是否製作 USB" value={usbRequired ? "yes" : "no"} onChange={(event) => updateUsbDetail(item.id, "required", event.target.value === "yes")} style={{ width: "100%", padding: "6px 5px", border: "1px solid var(--line)", borderRadius: 10, background: "#fff", color: "var(--ink)", fontSize: 12.5 }}><option value="yes">要</option><option value="no">不要</option></select>
                                <input type="number" min="0" aria-label="USB 數量" disabled={!usbRequired} value={(item.usb || {}).quantity || ""} onChange={(event) => updateUsbDetail(item.id, "quantity", event.target.value)} placeholder="數量" style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--line)", borderRadius: 10, background: "#fff", color: "var(--ink)", fontSize: 12.5 }} />
                                <input type="date" aria-label="USB 日期" disabled={!usbRequired} value={(item.usb || {}).date || ""} onChange={(event) => updateUsbDetail(item.id, "date", event.target.value)} style={{ width: "100%", minWidth: 0, padding: "6px 8px", border: "1px solid var(--line)", borderRadius: 10, background: "#fff", color: "var(--ink)", fontSize: 12.5 }} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>}
                  </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Kanban({ cases, dragOver, setDragOver, setDragId, moveCard, dirColor }) {
  return (
    <div className="stack" style={{ maxWidth: "none" }}>
      <div className="row" style={{ gap: 16, flexWrap: "wrap" }}>{TYPE_ORDER.map((type) => <div key={type} className="row" style={{ fontSize: 13 }}><span style={{ width: 10, height: 10, borderRadius: 3, background: TYPES[type].color }} />{TYPES[type].label}</div>)}</div>
      <div className="kanban">{STATUS_ORDER.map((status) => {
        const cards = [];
        cases.forEach((item) => TYPE_ORDER.forEach((type) => {
          if (item.deliverables[type] === status) cards.push({ item, type });
        }));
        return (
          <div key={status} className="kanban-col" onDragOver={(event) => { event.preventDefault(); setDragOver(status); }} onDrop={() => moveCard(status)}>
            <div className="row" style={{ marginBottom: 10 }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: STATUS[status].color }} /><span style={{ fontWeight: 600 }}>{STATUS[status].label}</span><span className="chip" style={{ marginLeft: "auto", color: STATUS[status].color, background: STATUS[status].bg }}>{cards.length}</span></div>
            <div className="kanban-body" style={{ background: dragOver === status ? "rgba(23,21,14,.04)" : "transparent", border: `1.5px dashed ${dragOver === status ? "#17150e" : "transparent"}` }}>
              {cards.map(({ item, type }) => <div key={`${item.id}:${type}`} className="kanban-card" draggable onDragStart={() => setDragId(`${item.id}:${type}`)} onDragEnd={() => setDragOver(null)}><div className="chip" style={{ background: TYPES[type].grad, color: "#17150e", marginBottom: 10 }}>{TYPES[type].label}</div><div style={{ fontWeight: 500, lineHeight: 1.35 }}>{item.name}</div><div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 8 }}>{fmt(item.date)} · <span style={{ color: dirColor(item.director), fontWeight: 600 }}>{item.director}</span></div></div>)}
            </div>
          </div>
        );
      })}</div>
    </div>
  );
}

function Staff({ cases, options, selected, setSelected, dirColor }) {
  const list = cases.filter((item) => item.director === selected);
  return <div className="staff-layout"><div className="card" style={{ padding: 14 }}>{options.directors.map((name) => <button key={name} className={`nav-button ${name === selected ? "active" : ""}`} style={{ justifyContent: "space-between", marginBottom: 8 }} onClick={() => setSelected(name)}><span>{name}</span><span>{cases.filter((item) => item.director === name).length}</span></button>)}</div><div className="card"><PanelTitle title={`${selected} 承接案件`} /><div className="stat-grid" style={{ marginBottom: 18 }}><Stat label="承接案件" value={list.length} sub="累計" /><Stat label="已完成" value={list.filter((item) => overall(item) === "done").length} sub="完成案件" /><Stat label="進行中" value={list.filter((item) => overall(item) === "doing").length} sub="尚未完成" /></div>{list.map((item) => <div key={item.id} className="row" style={{ padding: "13px 0", borderTop: "1px solid var(--line)" }}><div style={{ flex: 1 }}><div style={{ fontWeight: 500 }}>{item.name}</div><div style={{ fontSize: 13, color: "var(--muted)" }}>{fmt(item.date)}</div></div><div className="row" style={{ gap: 6 }}>{typeChips(item)}</div></div>)}</div></div>;
}

function Monthly({ cases, options, reportMonth, setReportMonth, reportGroupBy, setReportGroupBy, dirColor, shooterColor }) {
  const [year, month] = reportMonth.split("-");
  const list = cases.filter((item) => item.date.startsWith(reportMonth)).sort((a, b) => a.date.localeCompare(b.date));
  const counts = {};
  list.forEach((item) => counts[item.director] = (counts[item.director] || 0) + 1);
  const max = Math.max(1, ...Object.values(counts));
  const grouped = {};
  list.forEach((item) => {
    const key = reportGroupBy === "shooter" ? item.shooter : reportGroupBy === "caseAttribute" ? (item.caseAttribute || "一般") : item.director;
    grouped[key] = grouped[key] || [];
    grouped[key].push(item);
  });
  const shift = (step) => {
    const date = new Date(Number(year), Number(month) - 1 + step, 1);
    setReportMonth(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`);
  };
  return <div className="stack" style={{ maxWidth: 1180, gap: 18 }}><div className="row" style={{ justifyContent: "space-between" }}><button className="icon-button" title="上一個月" aria-label="上一個月" onClick={() => shift(-1)}><ChevronLeft size={20} /></button><div className="page-title">{year} 年 {month} 月</div><button className="icon-button" title="下一個月" aria-label="下一個月" onClick={() => shift(1)}><ChevronRight size={20} /></button></div><div className="stat-grid"><Stat label="本月案件總數" value={list.length} sub="單月累計" /><Stat label="已完成" value={list.filter((item) => overall(item) === "done").length} sub="完成狀態" /><Stat label="進行中" value={list.filter((item) => overall(item) === "doing").length} sub="製作中" /></div><div className="grid-2"><div className="card"><PanelTitle title="禮儀師案件數" />{options.directors.map((name) => <div key={name} style={{ marginBottom: 12 }}><div className="row" style={{ justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: dirColor(name), fontWeight: 600 }}>{name}</span><span>{counts[name] || 0}</span></div><div style={{ height: 9, background: "var(--surface2)", borderRadius: 9999 }}><div style={{ height: "100%", width: `${((counts[name] || 0) / max) * 100}%`, background: "#6883a1", borderRadius: 9999 }} /></div></div>)}</div><div className="card"><PanelTitle title="分組明細" meta={<span className="row" style={{ gap: 6, flexWrap: "wrap" }}><button className="mini-button" aria-pressed={reportGroupBy === "director"} onClick={() => setReportGroupBy("director")}>依禮儀師</button><button className="mini-button" aria-pressed={reportGroupBy === "shooter"} onClick={() => setReportGroupBy("shooter")}>依拍攝者</button><button className="mini-button" aria-pressed={reportGroupBy === "caseAttribute"} onClick={() => setReportGroupBy("caseAttribute")}>依案件屬性</button></span>} />{!list.length && <EmptyState title="本月尚無案件" />}{Object.entries(grouped).map(([name, items]) => <div key={name} style={{ borderTop: "1px solid var(--line)", padding: "12px 0" }}><div style={{ color: reportGroupBy === "shooter" ? shooterColor(name) : reportGroupBy === "caseAttribute" ? "var(--ink)" : dirColor(name), fontWeight: 600 }}>{name} · {items.length} 場</div>{items.map((item) => <div key={item.id} style={{ fontSize: 13, color: "var(--muted)", marginTop: 6 }}>{fmt(item.date)} · {item.name} · {item.location}</div>)}</div>)}</div></div><div className="table"><div className="table-head" style={{ gridTemplateColumns: "1.4fr 120px 110px 110px 100px" }}><div>案名</div><div>日期</div><div>禮儀師</div><div>拍攝者</div><div>狀態</div></div>{list.map((item) => <div key={item.id} className="table-row" style={{ gridTemplateColumns: "1.4fr 120px 110px 110px 100px" }}><div>{item.name}</div><div>{fmt(item.date)}</div><div style={{ color: dirColor(item.director), fontWeight: 600 }}>{item.director}</div><div style={{ color: shooterColor(item.shooter), fontWeight: 600 }}>{item.shooter}</div><div>{statusChip(overall(item))}</div></div>)}</div></div>;
}

function Annual({ cases, annualYear, setAnnualYear }) {
  const yearCases = cases.filter((item) => item.date.startsWith(annualYear));
  const counts = Array.from({ length: 12 }, (_, index) => cases.filter((item) => item.date.startsWith(`${annualYear}-${String(index + 1).padStart(2, "0")}`)).length);
  const max = Math.max(1, ...counts);
  const venueCounts = {};
  yearCases.forEach((item) => item.venueStyles.forEach((venue) => venueCounts[venue] = (venueCounts[venue] || 0) + 1));
  return <div className="stack" style={{ maxWidth: 1180, gap: 18 }}><div className="row" style={{ justifyContent: "space-between" }}><button className="icon-button" title="上一年" aria-label="上一年" onClick={() => setAnnualYear(String(Number(annualYear) - 1))}><ChevronLeft size={20} /></button><div className="page-title">{annualYear} 年</div><button className="icon-button" title="下一年" aria-label="下一年" onClick={() => setAnnualYear(String(Number(annualYear) + 1))}><ChevronRight size={20} /></button></div><div className="stat-grid"><Stat label="年度案件總數" value={yearCases.length} sub="全年累計" /><Stat label="已完成" value={yearCases.filter((item) => overall(item) === "done").length} sub="完成案件" /><Stat label="回頭件比例" value={yearCases.length ? `${Math.round((yearCases.filter((item) => item.returning).length / yearCases.length) * 100)}%` : "0%"} sub="回流客戶" /></div><div className="grid-2"><div className="card"><PanelTitle title="每月案件數" /><div className="bar-chart">{counts.map((count, index) => <div className="bar-item" key={index}><div style={{ fontSize: 12, fontWeight: 600 }}>{count}</div><div style={{ width: "100%", height: `${Math.round((count / max) * 150)}px`, minHeight: count ? 6 : 0, background: "#6883a1", borderRadius: "8px 8px 0 0" }} /><div style={{ fontSize: 12, color: "var(--muted)" }}>{index + 1}月</div></div>)}</div></div><div className="card"><PanelTitle title="會場樣式分布" />{!Object.keys(venueCounts).length && <EmptyState title="本年度尚無會場紀錄" />}{Object.entries(venueCounts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([name, count]) => <div key={name} style={{ marginBottom: 12 }}><div className="row" style={{ justifyContent: "space-between", marginBottom: 6 }}><span>{name}</span><span>{count}</span></div><div style={{ height: 9, background: "var(--surface2)", borderRadius: 9999 }}><div style={{ height: "100%", width: `${(count / Math.max(1, ...Object.values(venueCounts))) * 100}%`, background: "#aa886d", borderRadius: 9999 }} /></div></div>)}</div></div></div>;
}

function Settings({ options, setOptions, newOption, setNewOption }) {
  const groups = [["religions", "宗教", "案件建檔的宗教下拉選單", "新增宗教…"], ["caseAttributes", "案件屬性", "案件建檔的案件屬性下拉選單", "新增案件屬性…"], ["locations", "地點", "建檔表單的地點下拉選單", "新增地點…"], ["venueStyles", "會場樣式", "可複選的會場佈置樣式", "新增會場樣式…"], ["extras", "其他", "可複選的陣頭、供品等項目", "新增其他項目…"], ["directors", "禮儀師", "禮儀師下拉選單", "新增禮儀師…"], ["assistants", "跟件助理", "跟件助理下拉選單", "新增助理…"], ["emcees", "司儀", "司儀下拉選單", "新增司儀…"], ["shooters", "拍攝者", "建檔表單的拍攝者下拉選單", "新增拍攝者…"]];
  return <div className="settings-grid">{groups.map(([key, title, desc, placeholder]) => <div className="card" key={key}><div style={{ fontSize: 18, fontWeight: 400, letterSpacing: -0.3 }}>{title}</div><div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4, marginBottom: 14 }}>{desc}</div><div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>{options[key].map((item) => <span key={item} className="chip" style={{ background: "var(--surface2)", color: "var(--ink)" }}>{item}<button style={{ marginLeft: 8, border: "none", background: "transparent", cursor: "pointer", color: "var(--muted)" }} onClick={() => setOptions((current) => ({ ...current, [key]: current[key].filter((value) => value !== item) }))}>×</button></span>)}</div><div className="row"><input className="search" aria-label={`新增${title}`} value={newOption[key] || ""} onChange={(event) => setNewOption((current) => ({ ...current, [key]: event.target.value }))} placeholder={placeholder} /><button className="primary-button" style={{ padding: "0 22px", alignSelf: "stretch" }} onClick={() => { const value = (newOption[key] || "").trim(); if (!value) return; setOptions((current) => ({ ...current, [key]: [...current[key], value] })); setNewOption((current) => ({ ...current, [key]: "" })); }}>新增</button></div></div>)}</div>;
}

function SettingsEditor({ options, setOptions, newOption, setNewOption }) {
  const groups = [
    ["religions", "宗教", "案件建檔的宗教下拉選單", "新增宗教…"],
    ["caseAttributes", "案件屬性", "案件建檔的案件屬性下拉選單", "新增案件屬性…"],
    ["locations", "地點", "建檔表單的地點下拉選單", "新增地點…"],
    ["venueStyles", "會場樣式", "可複選的會場佈置樣式", "新增會場樣式…"],
    ["extras", "其他", "可複選的陣頭、供品等項目", "新增其他項目…"],
    ["directors", "禮儀師", "禮儀師下拉選單", "新增禮儀師…"],
    ["assistants", "跟件助理", "跟件助理下拉選單", "新增助理…"],
    ["emcees", "司儀", "司儀下拉選單", "新增司儀…"],
    ["shooters", "拍攝者", "建檔表單的拍攝者下拉選單", "新增拍攝者…"]
  ];

  const moveOption = (key, index, offset) => {
    setOptions((current) => {
      const list = [...(current[key] || [])];
      const nextIndex = index + offset;
      if (nextIndex < 0 || nextIndex >= list.length) return current;
      [list[index], list[nextIndex]] = [list[nextIndex], list[index]];
      return { ...current, [key]: list };
    });
  };

  const renameOption = (key, oldValue) => {
    const nextValue = window.prompt("修改選項名稱", oldValue);
    if (nextValue === null) return;
    const cleanValue = nextValue.trim();
    if (!cleanValue || cleanValue === oldValue) return;
    setOptions((current) => ({
      ...current,
      [key]: (current[key] || []).map((value) => value === oldValue ? cleanValue : value)
    }));
  };

  const removeOption = (key, value) => {
    setOptions((current) => ({
      ...current,
      [key]: (current[key] || []).filter((item) => item !== value)
    }));
  };

  const addOption = (key) => {
    const value = (newOption[key] || "").trim();
    if (!value) return;
    setOptions((current) => ({ ...current, [key]: [...(current[key] || []), value] }));
    setNewOption((current) => ({ ...current, [key]: "" }));
  };

  return (
    <div className="settings-grid">
      {groups.map(([key, title, desc, placeholder]) => (
        <div className="card" key={key}>
          <div style={{ fontSize: 18, fontWeight: 400, letterSpacing: -0.3 }}>{title}</div>
          <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 4, marginBottom: 14 }}>{desc}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
            {(options[key] || []).map((item, index) => (
              <div key={`${item}-${index}`} className="option-row">
                <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item}</span>
                <button className="icon-button" title="上移" aria-label={`上移 ${item}`} onClick={() => moveOption(key, index, -1)} disabled={index === 0}><ArrowUp size={15} /></button>
                <button className="icon-button" title="下移" aria-label={`下移 ${item}`} onClick={() => moveOption(key, index, 1)} disabled={index === (options[key] || []).length - 1}><ArrowDown size={15} /></button>
                <button className="icon-button" title="修改名稱" aria-label={`修改 ${item}`} onClick={() => renameOption(key, item)}><Pencil size={15} /></button>
                <button className="icon-button danger" title="刪除選項" aria-label={`刪除 ${item}`} onClick={() => removeOption(key, item)}><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
          <div className="row">
            <input className="search" aria-label={`新增${title}`} value={newOption[key] || ""} onChange={(event) => setNewOption((current) => ({ ...current, [key]: event.target.value }))} placeholder={placeholder} />
            <button className="primary-button" style={{ padding: "0 22px", alignSelf: "stretch" }} onClick={() => addOption(key)}>新增</button>
          </div>
        </div>
      ))}
    </div>
  );
}
